import * as React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { Link } from 'react-router';
import { FlexCol, FlexRowCenterVertical } from '../../Defaults/Flexbox/Flexbox';
import { TextP } from '../../Defaults/Text';
import { useState } from 'react';
import { getProductDefaultInfo } from '../../../utilities/selectors';
import { OutsideClick } from '../../..';
import { __ } from 'react-i18n';
import { formatPrice } from '../../../utilities';
import { StockInfo } from '../Product/StockInfo';

interface Props {
  id?: string;
  name?: string;
  label?: string;
  value: {
    image: string;
    name: string;
    plu: string;
    price: number;
    currency: string;
  };
  maxWidth?: number;
  marginBottom?: number;
  products: any;
  autoComplete?: string;
  onChange: (e: any) => void;
  onBlur?: () => void;
  apiUrl: string;
  showMoc?: boolean;
  showWithVat?: boolean;
  loadingPrices?: boolean;
}

function Select({
  products,
  value,
  maxWidth = 320,
  marginBottom = 0,
  label,
  showMoc,
  showWithVat,
  loadingPrices,
}: Props) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const defaultOption = value;
  const changeDropdownState = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const closeSelect = () => {
    setIsDropdownOpen(false);
  };

  const renderOption = (product, key) => {
    if (!product) {
      return null;
    }
    const info = getProductDefaultInfo(product, '', showMoc, showWithVat);

    const {
      name,
      picture,
      plu,
      price,
      currency,
      url,
      availability,
      availid,
      onStock,
      isNew
    } = info;

    return (
      <StyledLink to={url}>
        <StyledOption key={key}>
          <FlexRowCenterVertical>
            <Image
              alt={`${__('Obrázok produktu')} ${name}`}
              width={46}
              height={46}
              src={picture}
            />
            <FlexCol>
              <Name>{name}</Name>
              <Plu>{plu}</Plu>
              <StockInfo
                avail_id={availid}
                availability={availability}
                onStockCount={0}
                isNew={!!isNew}
              />
            </FlexCol>
          </FlexRowCenterVertical>
          <PriceFlexCol>
            <TextP>
              {loadingPrices
                ? ''
                : price
                ? formatPrice(price, currency, 2)
                : __('Na požiadanie')}
            </TextP>
          </PriceFlexCol>
        </StyledOption>
      </StyledLink>
    );
  };

  return (
    <Wrapper marginBottom={marginBottom} maxWidth={maxWidth}>
      {label && <SelectLabel label={label}>{label}</SelectLabel>}
      <OutsideClick handleFunction={closeSelect}>
        <StyledDropdown onClick={changeDropdownState}>
          <StyledOption>
            <FlexRowCenterVertical>
              <Image
                alt={`${__('Obrázok produktu')} ${defaultOption.name}`}
                width={46}
                height={46}
                src={defaultOption.image}
              />
              <FlexCol>
                <Name>{defaultOption.name}</Name>
                <Plu>{defaultOption.plu}</Plu>
              </FlexCol>
            </FlexRowCenterVertical>
          </StyledOption>
          <Options isOpen={isDropdownOpen}>
            {products && products.length > 0 ? (
              products.map((p: any, key) => renderOption(p, key))
            ) : (
              <StyledOption>
                <FlexRowCenterVertical>
                  <FlexCol>
                    <Name>{__(`Žiadne alternatívne produkty`)}</Name>
                  </FlexCol>
                </FlexRowCenterVertical>
              </StyledOption>
            )}
          </Options>
        </StyledDropdown>
      </OutsideClick>
    </Wrapper>
  );
}

const Wrapper = styled(FlexCol)<{ maxWidth: number; marginBottom: number }>`
  width: 100%;
  color: #aaaaaa;
  position: relative;
  margin-right: ${rem(32)};
  margin-bottom: ${({ marginBottom }) => rem(marginBottom)};

  &:focus-within {
    color: ${props => props.theme.colors.btnPrimary};
  }

  ${({ theme }) => theme.mediab.s450`
    max-width: initial;
  `}
`;

const StyledOption = styled(FlexRowCenterVertical)`
  padding: ${rem(9)};
  justify-content: space-between;
`;

const SelectLabel: any = styled(TextP)`
  font-size: ${rem(12)};
  font-family: ${({ theme }) => theme.font.tertiary};
  line-height: ${rem(20)};
  margin-bottom: ${rem(2)};
`;
const StyledDropdown = styled.div`
  width: 100%;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid #cccccc;
  border-radius: 4px;
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${rem(14)};
  appearance: none;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.08);
  font-weight: 300;
  display: flex;
  align-items: center;

  background-image: url('/images/dsi/arrow-down.svg');
  background-position: 95% 50%;
  background-size: 16px 16px;
  background-repeat: no-repeat;
`;

const Options = styled.div<{ isOpen: boolean }>`
  width: 100%;
  left: 0;
  top: ${rem(66)};
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid #cccccc;
  border-top: 0;
  border-radius: 4px;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.08);
  position: absolute;
  z-index: 1;
  max-height: ${rem(192)};
  overflow: scroll;
  overflow-x: hidden;
`;

const Name = styled(TextP)`
  font-size: ${rem(14)};
  font-weight: 500;
  max-width: ${rem(400)};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  ${({ theme }) => theme.mediab.l1150`
    max-width: ${rem(300)};
  `}
  ${({ theme }) => theme.mediab.l1050`
    max-width: ${rem(250)};
  `}
  ${({ theme }) => theme.mediab.l925`
    max-width: ${rem(350)};
  `}
  ${({ theme }) => theme.mediab.m580`
    max-width: ${rem(200)};
  `}
  ${({ theme }) => theme.mediab.s450`
    max-width: ${rem(95)};
  `}
`;

const Plu = styled(TextP)`
  font-size: ${rem(14)};
  color: ${({ theme }) => theme.blogColors.gray};
`;

const Image = styled.img`
  margin-right: ${rem(16)};
`;

const StyledLink = styled(Link)`
  text-decoration: none;
`;

const PriceFlexCol = styled(FlexCol)`
  margin-left: ${rem(8)};
`;

export { Select };
